*,
*::before,
*::after {
  box-sizing: border-box;
}

:host {
  position: relative;

  /* inline-flex 让内层按钮在宿主内完全居中，避免继承的行高撑高宿主、导致图标偏移 */
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

:host([full]) {
  display: block;
  width: 100%;
}

:host([disabled]) {
  cursor: not-allowed;
}

button {
  cursor: pointer;
  user-select: none;

  position: relative;

  overflow: clip;
  display: inline-flex;
  gap: var(--wui-button-gap, 8px);
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: var(--wui-button-width, max-content);
  min-width: var(--wui-control-size, 40px);
  height: var(--wui-control-size, 40px);
  padding: 0 var(--wui-button-px, 12px);
  border: 1px solid transparent;
  border-radius: calc(infinity * 1px);

  /* 原生 button 不继承字体；显式继承以对齐宿主排版，避免 UA 默认 Arial 差异 */
  font: inherit;
  font-size: var(--wui-font-size, 14px);
  line-height: 1;
  color: var(--wui-button-color);
  white-space: nowrap;

  background-color: var(--wui-button-bg);

  transition: background-color var(--wui-duration-trigger, 160ms) ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:focus-visible {
    outline: var(--wui-focus-ring-width, 3px) solid var(--wui-color-focus-ring, rgb(0 136 255 / 0.4));
    outline-offset: 2px;
  }
}

@media (hover: hover) and (pointer: fine) {
  button:hover:not(:disabled) {
    background-color: var(--wui-button-bg-hover);
  }
}

button:active:not(:disabled) {
  background-color: var(--wui-button-bg-active);
}

@media (prefers-reduced-motion: reduce) {
  button {
    transition: background-color var(--wui-duration-trigger, 160ms) ease;
  }
}

/* ===== Full width ===== */

:host([full]) button {
  display: flex;
  width: 100%;
}

/* ===== Icon mode ===== */

:host([icon]) button {
  padding: 0;
}

/* ===== Variant: primary (默认) ===== */

:host([variant='primary']) {
  --wui-button-color: var(--wui-color-on-accent, #fff);
  --wui-button-bg: var(--wui-color-accent, #08f);
  --wui-button-bg-hover: color-mix(in srgb, var(--wui-color-accent, #08f) 88%, var(--wui-color-text, #1b1b1b));
  --wui-button-bg-active: color-mix(in srgb, var(--wui-color-accent, #08f) 65%, var(--wui-color-text, #1b1b1b));
}

/* ===== Variant: secondary ===== */

:host([variant='secondary']) {
  --wui-button-color: var(--wui-color-text, #1b1b1b);
  --wui-button-bg: var(--wui-color-surface-control, #dfdfdf);
  --wui-button-bg-hover: color-mix(
    in srgb,
    var(--wui-color-surface-control, #dfdfdf) 90%,
    var(--wui-color-text, #1b1b1b)
  );
  --wui-button-bg-active: color-mix(
    in srgb,
    var(--wui-color-surface-control, #dfdfdf) 70%,
    var(--wui-color-text, #1b1b1b)
  );
}

/* ===== Variant: ghost ===== */

:host([variant='ghost']) {
  --wui-button-color: var(--wui-color-text, #1b1b1b);
  --wui-button-bg: transparent;
  --wui-button-bg-hover: var(
    --wui-color-state-layer-hover,
    color-mix(in srgb, var(--wui-color-text, #1b1b1b) 6%, transparent)
  );
  --wui-button-bg-active: var(
    --wui-color-state-layer-active,
    color-mix(in srgb, var(--wui-color-text, #1b1b1b) 15%, transparent)
  );
}

/* ===== Variant: danger ===== */

:host([variant='danger']) {
  --wui-button-color: var(--wui-color-danger, #dc2626);
  --wui-button-bg: color-mix(in srgb, var(--wui-color-danger, #dc2626) 18%, var(--wui-color-surface, #fff));
  --wui-button-bg-hover: color-mix(in srgb, var(--wui-color-danger, #dc2626) 26%, var(--wui-color-surface, #fff));
  --wui-button-bg-active: color-mix(in srgb, var(--wui-color-danger, #dc2626) 45%, var(--wui-color-surface, #fff));
}

/* ===== Variant: glass (玻璃) ===== */

/* .wui-glass 样式由 @/assets/glass.css 提供 */
:host([variant='glass']) {
  --wui-button-color: var(--wui-color-text, #1b1b1b);

  /* standalone glass 底色由 .wui-glass 提供；hover/active 需要还原玻璃底，避免被按钮状态色整块替换 */
  --wui-button-bg: transparent;
  --wui-button-bg-hover: var(
    --wui-color-surface-glass-hover,
    color-mix(in srgb, var(--wui-color-text, #1b1b1b) 6%, var(--wui-color-surface-glass, rgb(250 250 250 / 0.34)))
  );
  --wui-button-bg-active: var(
    --wui-color-surface-glass-active,
    color-mix(in srgb, var(--wui-color-text, #1b1b1b) 15%, var(--wui-color-surface-glass, rgb(250 250 250 / 0.34)))
  );
}

/* ===== Group context ===== */

button.is-grouped {
  --wui-button-color: var(--wui-color-text, #1b1b1b);
  --wui-button-bg: transparent;
  --wui-button-bg-hover: var(
    --wui-color-state-layer-hover,
    color-mix(in srgb, var(--wui-color-text, #1b1b1b) 6%, transparent)
  );
  --wui-button-bg-active: var(
    --wui-color-state-layer-active,
    color-mix(in srgb, var(--wui-color-text, #1b1b1b) 15%, transparent)
  );
}

.group-divider {
  position: absolute;
  z-index: 1;
  top: 50%;
  right: -2px;
  transform: translateY(-50%);

  width: 1px;
  height: 24px;

  background-color: var(--wui-color-border, rgb(0 0 0 / 0.1));
}

.group-divider.vertical {
  top: auto;
  right: auto;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);

  width: 24px;
  height: 1px;
}
